// Import necessary dependencies and components
import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import {
  Row,
  Col,
  Button,
  ButtonGroup,
  Dropdown,
  Badge,
  Nav,
} from "react-bootstrap";
import ClickBack from "../../../components/buttons/ClickBack";


import IconShow  from "../../../images/fi_eye.png";
import IconEdit  from "../../../images/edit.png";
import Download from "../../../images/download.svg";

import { fetchAllFournisseurCommandes, fetchAllFournisseurCommandesForCalendar, fetchAllFournisseurCommandesForKanban, fetchAvailableDeliveryDates } from "../CommandesFournisseurs/DataService";
import { urlPdf } from "../../../utils/ApiConfig";
import { format } from "date-fns";
import DynamicTable from "../../../components/DynamicTable";
import SearchBar from "../../../components/SearchBar";
import ElementsPerPageDropdown from "../Component/ElementsPerPageDropdown";
import CommandeStatusDropdown from "../Component/CommandeStatusDropdown";
import LivreurDropdown from "../Component/LivreurDropdown";
import ClientTypeDropdown from "../Component/ClientTypeDropdown";
import DeliveryDatePicker from "../Component/DeliveryDatePicker";
import KanbanView from "./KanbanView";
import CalendarView from "./CalendarView";
import { badgePaiement } from "./badgePaiement";
import { totalTtcCommande, detailTotalCommande } from "./totalCommande";




// Le retard vient de l'API (RetardLivraisonService), règle unique : échéance =
// fin du créneau promis + manutention prévue. Le navigateur ne le recalcule plus :
// la liste et le kanban se contredisaient, chacun avec sa propre marge.
const getDelaiStatus = (order) => {
  if (order.StatuCommande === 2 || order.StatuCommande === 6) return null;
  const r = order.retard_livraison;
  if (!r?.echeance) return null;

  // « 2026-10-09 12:00:00 » → « 12:00 », sans conversion de fuseau.
  const heure = (v) => String(v || "").slice(11, 16);
  const titre =
    `Échéance ${heure(r.echeance)} : créneau jusqu'à ${heure(r.fin_creneau)}` +
    (r.minutes_manutention > 0 ? ` + ${r.minutes_manutention} min de manutention prévue` : "");

  if (r.en_retard) {
    return { label: `En retard (${r.minutes} min)`, bg: "danger", titre };
  }
  // `minutes` est négatif tant que l'échéance n'est pas atteinte.
  if (r.minutes !== null && r.minutes >= -120) {
    return { label: "Imminent", bg: "warning", titre };
  }
  return { label: "OK", bg: "success", titre };
};

// Define the functional component
function ListCommandesAllFournisseur(props) {
  // State to hold user data
  const [commandesData, setCommandesData] = useState(null);
  const [calendarData, setCalendarData] = useState(null); // Données séparées pour le calendrier
  const [kanbanData, setKanbanData] = useState(null); // Données séparées pour le kanban
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nbr, setNbr] = useState(1);
  const [nbElement, setNbElement] = useState(10);

  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedLivreur, setSelectedLivreur] = useState("");
  const [selectedClientType, setSelectedClientType] = useState("");
  // `?date=AAAA-MM-JJ` préremplit le filtre : c'est ce qu'envoient les cartes
  // « Aujourd'hui » / « Demain » du dashboard. Date locale, pas `new Date(iso)`,
  // qui la lirait en UTC.
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(new URLSearchParams(window.location.search).get("date") || "");
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  });
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState([]);
  const [enRetard, setEnRetard] = useState(false);
  // Manutention laissée en "a_confirmer" : tant qu'elle ne l'est pas, elle est
  // facturée 0 €, et le client s'est vu promettre une vérification à la commande.
  const [manutentionAConfirmer, setManutentionAConfirmer] = useState(false);
  // Chiffrée par un livreur depuis la fiche reçue par SMS, et pas encore relue.
  // Son montant part en facturation qu'on l'ait relu ou non : la relecture n'a
  // de valeur que dans la fenêtre qui précède l'émission de la facture.
  const [manutentionAValider, setManutentionAValider] = useState(false);
  const [loadAllDelivered, setLoadAllDelivered] = useState(false);
  // Coche par defaut : une commande au paiement carte echoue ou annule
  // ressemblait a une commande a livrer, jusque dans le kanban du dispatch.
  const [masquerPaiementsEchoues, setMasquerPaiementsEchoues] = useState(true);
  const [hiddenColumns, setHiddenColumns] = useState(
    () => JSON.parse(localStorage.getItem('commandesHiddenColumns') || 'null')
      ?? ["poids_label", "DateCommande", "employe_email", "temps_estime"]
  );

  const optionalColumns = [
    { accessor: "StatuCommande",   label: "Statut commande" },
    { accessor: "type_livraison",  label: "Mode livraison" },
    { accessor: "poids_label",     label: "Poids" },
    { accessor: "DateCommande",    label: "Créé le" },
    { accessor: "employe_email",   label: "Commanditaire" },
    { accessor: "temps_estime",    label: "Temps trajet" },
  ];

  const toggleColumn = (accessor) => {
    setHiddenColumns(prev => {
      const next = prev.includes(accessor)
        ? prev.filter(c => c !== accessor)
        : [...prev, accessor];
      localStorage.setItem('commandesHiddenColumns', JSON.stringify(next));
      return next;
    });
  };

  // Gestion de la vue active (liste, kanban, calendrier)
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem('commandesView') || 'list';
  });

  const handleViewChange = (view) => {
    setActiveView(view);
    localStorage.setItem('commandesView', view);
  };

  const handleStatusChange = (newStatus) => {
        setSelectedStatus(newStatus);
        setNbr(1); // reset to first page
        };

  const handleLivreurChange = (newLivreur) => {
        setSelectedLivreur(newLivreur);
        setNbr(1); // reset to first page
        };

  const handleClientTypeChange = (newClientType) => {
        setSelectedClientType(newClientType);
        setNbr(1); // reset to first page
        };

  const handleDeliveryDateChange = (date) => {
        setSelectedDeliveryDate(date);
        setNbr(1); // reset to first page
        };

  const handleEnRetardChange = (event) => {
        setEnRetard(event.target.checked);
        setNbr(1); // reset to first page
        };

  const handleManutentionAConfirmerChange = (event) => {
        setManutentionAConfirmer(event.target.checked);
        setNbr(1); // reset to first page
        };

  const handleManutentionAValiderChange = (event) => {
        setManutentionAValider(event.target.checked);
        setNbr(1); // reset to first page
        };

  const handleOrderStatusChange = async (orderId, newStatus) => {
    try {
      // TODO: Implement API call to update order status
      // For now, just refetch the data to show the change
      // You'll need to add the actual API endpoint here
      console.log(`Updating order ${orderId} to status ${newStatus}`);

      // Refetch data after status change
      await fetchData();
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };


  // Cette fonction pour afficher liste des fournisseurs
  const fetchData = async () => {
    try {
      setLoading(true);
      const formattedDate = selectedDeliveryDate ? format(selectedDeliveryDate, "yyyy-MM-dd") : null;
      const data = await fetchAllFournisseurCommandes(nbr, searchTerm, nbElement, selectedStatus, selectedLivreur, formattedDate, enRetard, selectedClientType, manutentionAConfirmer, manutentionAValider, masquerPaiementsEchoues);
      setCommandesData(data);
    } catch (error) {
      console.error("Error lors de affiche liste fournisseur:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger toutes les commandes pour le calendrier (sans pagination)
  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const formattedDate = selectedDeliveryDate ? format(selectedDeliveryDate, "yyyy-MM-dd") : null;
      // Utiliser l'endpoint dédié au calendrier qui retourne toutes les commandes
      const data = await fetchAllFournisseurCommandesForCalendar(searchTerm, selectedStatus, selectedLivreur, formattedDate, enRetard, selectedClientType, masquerPaiementsEchoues);
      setCalendarData(data);
    } catch (error) {
      console.error("Error lors de affiche calendrier:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger toutes les commandes pour le kanban (sans pagination)
  const fetchKanbanData = async (forceLoadAll = false) => {
    try {
      setLoading(true);
      const formattedDate = selectedDeliveryDate ? format(selectedDeliveryDate, "yyyy-MM-dd") : null;
      // Limiter les commandes livrées à 20 par défaut, sauf si loadAllDelivered est true
      const limitDelivered = (loadAllDelivered || forceLoadAll) ? null : 20;
      // Utiliser l'endpoint dédié au kanban qui retourne toutes les commandes
      const data = await fetchAllFournisseurCommandesForKanban(searchTerm, selectedStatus, selectedLivreur, formattedDate, enRetard, selectedClientType, limitDelivered, masquerPaiementsEchoues);
      setKanbanData(data);
    } catch (error) {
      console.error("Error lors de affiche kanban:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger toutes les commandes livrées
  const handleLoadAllDelivered = () => {
    setLoadAllDelivered(true);
    fetchKanbanData(true);
  };

  // Fetch all available delivery dates (independent of pagination)
  const fetchDeliveryDates = async () => {
    try {
      const dates = await fetchAvailableDeliveryDates();
      setAvailableDeliveryDates(dates);
    } catch (error) {
      console.error("Error fetching delivery dates:", error);
    }
  };
    const handleNombreElementParPage = (nbElement) => {
      setNbElement(nbElement); // Update the state with the selected value
    };
    // Update search term and fetch data
    const handleSearch = (term) => {
      setSearchTerm(term);
      setNbr(1); // Reset to first page
    };
  // useEffect pour charger automatiquement les données lors du montage du composant
  useEffect(() => {
    fetchData(nbr); // Toujours charger pour liste/kanban/total
  }, [nbr, searchTerm, nbElement, selectedStatus, selectedLivreur, selectedDeliveryDate, enRetard, selectedClientType, manutentionAConfirmer, manutentionAValider, masquerPaiementsEchoues]);

  // useEffect pour charger les données du calendrier quand on passe en vue calendrier
  useEffect(() => {
    if (activeView === 'calendar') {
      fetchCalendarData();
    }
  }, [activeView, searchTerm, selectedStatus, selectedLivreur, selectedDeliveryDate, enRetard, selectedClientType, masquerPaiementsEchoues]);

  // useEffect pour charger les données du kanban quand on passe en vue kanban
  useEffect(() => {
    if (activeView === 'kanban') {
      fetchKanbanData();
    }
  }, [activeView, searchTerm, selectedStatus, selectedLivreur, selectedDeliveryDate, enRetard, selectedClientType, masquerPaiementsEchoues]);

  // useEffect pour charger les dates de livraison disponibles une seule fois
  useEffect(() => {
    fetchDeliveryDates();
  }, []); // Run only once on component mount

  // Colonnes mémorisées pour le composant DynamicTable
  const columns = useMemo(() => {
    return [
      {
        Header: "Id",
        accessor: "id",
        Cell: ({ value }) => <span>{value}</span>,
        style: { width: "100px", minWidth: "80px", maxWidth: "120px", textAlign: "center" },
      },
      {
        Header: "Type",
        accessor: "client_type",
        Cell: ({ value, row }) => {
          if (row.original.source === 3) {
            return <Badge className="bg-dark">🤝 Partenaire</Badge>;
          }
          const map = {
            fournisseur: ["bg-primary",            "🏢 Fournisseur"],
            particulier: ["bg-success",            "👤 Particulier"],
            entreprise:  ["bg-warning text-dark",  "💼 Entreprise"],
          };
          const [badgeClass, label] = map[value] || ["bg-secondary", `❓ ${value || "Inconnu"}`];
          return <Badge className={badgeClass}>{label}</Badge>;
        },
        style: { width: "130px", minWidth: "120px", maxWidth: "150px" },
      },
      {
        Header: "Statut",
        accessor: "StatuCommande",
        Cell: ({ value, row }) => {
          const map = {
            1: ["warning",   "En livraison"],
            2: ["success",   "Livré"],
            3: ["info",      "En préparation"],
            4: ["primary",   "En cours de validation"],
            6: ["danger",    "Annulé"],
          };
          const [bg, label] = map[value] || ["secondary", "Inconnu"];
          const paiement = badgePaiement(row.original);
          return (
            <>
              <Badge bg={bg}>{label}</Badge>
              {paiement && (
                <Badge bg={paiement.bg} text={paiement.text} className="d-block mt-1">
                  {paiement.label}
                </Badge>
              )}
            </>
          );
        },
      },
      {
        Header: "Mode",
        accessor: "type_livraison",
        Cell: ({ value }) => {
          const map = {
            Urgent:    ["danger",    "🔴 Urgent"],
            Planifiée: ["primary",   "📅 Planifiée"],
            Flex:      ["secondary", "Flex"],
          };
          const [bg, label] = map[value] || ["secondary", value || "—"];
          return value ? <Badge bg={bg}>{label}</Badge> : <span>—</span>;
        },
      },
      {
        Header: "Date livraison",
        accessor: "DateLivraison",
        Cell: ({ value, row }) => {
          const delai = getDelaiStatus(row.original);
          return (
            <div>
              <div>{value ? format(new Date(value), "dd/MM/yyyy HH:mm") : "—"}</div>
              {delai && <Badge bg={delai.bg} className="mt-1" title={delai.titre}>{delai.label}</Badge>}
            </div>
          );
        },
      },
      {
        Header: "Client",
        accessor: "fournisseur_profil.user_profil.RaisonSocial",
        Cell: ({ row }) => {
          const source = row.original.source;
          const clientType = row.original.client_type;
          const raisonSocial = row.original.fournisseur_profil?.user_profil?.RaisonSocial;
          const nom = row.original.NomClient;
          const prenom = row.original.PrenomClient;
          const fullName = [prenom, nom].filter(Boolean).join(' ');
          // Commandes partenaires : toujours NomClient + PrenomClient
          if (source === 3) return <span>{fullName || '—'}</span>;
          if (clientType === 'fournisseur') return <span>{raisonSocial || fullName || '—'}</span>;
          return <span>{fullName || raisonSocial || '—'}</span>;
        },
      },
      {
        Header: "Pick-up",
        accessor: "AdressePickUp",
        Cell: ({ value }) => {
          const ville = value?.split(",")[1]?.trim();
          return <span>{ville || "—"}</span>;
        },
      },
      {
        Header: "Livraison",
        accessor: "AdresseLivraison",
        Cell: ({ value }) => {
          const ville = value?.split(",")[1]?.trim();
          return <span>{ville || "—"}</span>;
        },
      },
      {
        // La colonne affichait livraisonHT : le transport seul, hors taxes. Sur
        // une commande à 23,88 € de transport et 48 € de manutention, l'écran
        // annonçait 19,90 €. Le total suit désormais la seule formule qu'utilise
        // la facturation — TTC + manutention_total_ttc — donc le montant lu ici
        // est celui que le client paiera.
        Header: "Total TTC",
        id: "total_ttc",
        // Accesseur calculé, et non "livraisonHT" : useSortBy est actif
        // (DynamicTable.js), et trier sur une valeur différente de celle
        // affichée est pire que ne pas trier du tout.
        accessor: (row) => totalTtcCommande(row),
        Cell: ({ row }) => {
          const { TTC, manutention_total_ttc, manutention_status } = row.original;
          const aUnMontant = Number(TTC || 0) > 0 || Number(manutention_total_ttc || 0) > 0;

          if (!aUnMontant && manutention_status !== "a_confirmer") {
            return <span>—</span>;
          }

          const detail = detailTotalCommande(row.original);

          return (
            <span>
              {totalTtcCommande(row.original).toFixed(2)} €
              {/* Manutention non chiffrée : son montant vaut 0 et c'est ce 0 qui
                  partira en facture. Le total affiché est un plancher, pas un prix. */}
              {detail?.aConfirmer ? (
                <>
                  {" "}
                  <span title="Manutention non chiffrée : le total n'est pas définitif">⚠</span>
                  <small className="d-block text-warning">manutention à confirmer</small>
                </>
              ) : detail ? (
                <small className="d-block text-muted">
                  transport {detail.transport.toFixed(2)} · manut. {detail.manutention.toFixed(2)}
                </small>
              ) : null}
            </span>
          );
        },
      },
      {
        Header: "Livreur",
        accessor: "livreur_profil",
        Cell: ({ value, row }) => {
          const nom = value?.nom;
          const prenom = value?.prenom;
          return (
            <span>
              {nom || prenom ? `${nom || ""} ${prenom || ""}`.trim() : "—"}
              {/* Le drapeau vient du serveur : c'est la même règle que celle qui
                  facture le supplément « deuxième personne », et la recopier ici
                  en ferait une version de plus, libre de diverger. */}
              {row.original.manutention_deux_personnes && (
                <Badge bg="warning" text="dark" className="d-block mt-1" title="Colis lourd (plus de 40 kg) : deux porteurs nécessaires">
                  2 livreurs
                </Badge>
              )}
              {/* Le livreur a chiffré la manutention depuis la fiche reçue par
                  SMS et personne ne l'a relue. Le montant part en facturation
                  quand même : la relecture n'a de valeur qu'avant l'émission. */}
              {row.original.manutention_a_valider && (
                <Badge bg="info" className="d-block mt-1" title="Manutention chiffrée par le livreur, pas encore relue par l'agence">
                  Saisie livreur à valider
                </Badge>
              )}
            </span>
          );
        },
      },
      // Colonnes optionnelles (toggleables)
      {
        Header: "Poids",
        accessor: "poids_label",
        Cell: ({ value, row }) => <span>{value || row.original.poids || "—"}</span>,
      },
      {
        Header: "Créé le",
        accessor: "DateCommande",
        Cell: ({ value }) => value ? format(new Date(value), "dd/MM/yyyy HH:mm") : "—",
      },
      {
        Header: "Commanditaire",
        accessor: "employe_email",
        Cell: ({ row }) => {
          const nom = row.original.employe_nom_complet;
          const email = row.original.emailFournisseur;
          // La société du compte, sous la personne. `nom_societe` est calculé par
          // l'API (RaisonSocial, sinon la société saisie à l'inscription).
          const societe = row.original.fournisseur_profil?.user_profil?.nom_societe;
          return (
            <span style={{ fontSize: "0.85em" }}>
              {nom || email || "—"}
              {societe && <div className="text-muted">{societe}</div>}
            </span>
          );
        },
      },
      {
        Header: "Trajet",
        accessor: "temps_estime",
        Cell: ({ value }) => value ? `${value} min` : "—",
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div>
            <a href={`/commande-fournisseur/show/${row.original.id}`} className="p-2">
              <img src={IconShow} width="20px" alt="Voir" />
            </a>
            <a href={`/commande-fournisseur/edit/${row.original.id}`} className="p-2">
              <img src={IconEdit} width="20px" alt="Modifier" />
            </a>
            <a href={`${urlPdf}/Bordereau/${row?.original.Bordereau}`} target="_blank" rel="Bordereaux">
              <img src={Download} alt="Download" />
            </a>
          </div>
        ),
      },
    ];
  }, []);

  const visibleColumns = useMemo(
    () => columns.filter(col => !col.accessor || !hiddenColumns.includes(col.accessor)),
    [columns, hiddenColumns]
  );

  // Render loading message while data is being fetched
  if (!commandesData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  // Render the component
  return (
    <Container>
      <div className="commandesFournisseursList">
        {/* Breadcrumb navigation */}
        <p className="h6">Accueil / Livraisons </p>

        {/* Header and filters */}
        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            {/* Display dynamic title */}
            <h1 className="p-1">Livraisons</h1>
          </Col>
          {/* add button */}
          <Col
            className="d-flex justify-content-end align-items-center"
            style={{ alignContent: "right" }}
          >
            <a className="btn btn-primary" href="/commande-fournisseur/add">
              Ajouter Livraison
            </a>
          </Col>
        </Row>

        {/* Tabs de sélection de vue */}
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link
              active={activeView === 'list'}
              onClick={() => handleViewChange('list')}
              style={{ cursor: 'pointer' }}
            >
              📋 Liste
              {commandesData?.total && <Badge bg="secondary" className="ms-2">{commandesData.total}</Badge>}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeView === 'kanban'}
              onClick={() => handleViewChange('kanban')}
              style={{ cursor: 'pointer' }}
            >
              📊 Kanban
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeView === 'calendar'}
              onClick={() => handleViewChange('calendar')}
              style={{ cursor: 'pointer' }}
            >
              📅 Calendrier
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Filtres (affichés pour toutes les vues) */}
        <Row className="pt-4 pb-2 align-items-center">
        {/* Pagination uniquement pour la vue liste */}
        {activeView === 'list' && (
          <Col xs="auto">
            <ElementsPerPageDropdown
              nbElement={nbElement}
              onNbElementChange={handleNombreElementParPage}
            />
          </Col>
        )}

	   <Col xs="auto">
            <CommandeStatusDropdown
              selectedStatus={selectedStatus}
              onStatusChange={handleStatusChange}
            />
          </Col>

          <Col xs="auto">
            <LivreurDropdown
              selectedLivreur={selectedLivreur}
              onLivreurChange={handleLivreurChange}
            />
          </Col>

          <Col xs="auto">
            <ClientTypeDropdown
              selectedClientType={selectedClientType}
              onClientTypeChange={handleClientTypeChange}
            />
          </Col>

          <DeliveryDatePicker
            selectedDate={selectedDeliveryDate}
            onDateChange={handleDeliveryDateChange}
            availableDates={availableDeliveryDates}
          />

          <Col xs="auto" className="d-flex align-items-center">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="enRetardCheckbox"
                checked={enRetard}
                onChange={handleEnRetardChange}
              />
              <label className="form-check-label" htmlFor="enRetardCheckbox">
                En retard
              </label>
            </div>
          </Col>

          <Col xs="auto" className="d-flex align-items-center">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="manutentionAConfirmerCheckbox"
                checked={manutentionAConfirmer}
                onChange={handleManutentionAConfirmerChange}
              />
              <label className="form-check-label" htmlFor="manutentionAConfirmerCheckbox">
                Manutention à confirmer
              </label>
            </div>
          </Col>

          {/* Pendant de la case ci-dessus, pour l'autre moitié du problème :
              là, la manutention est chiffrée mais par le livreur, et personne ne
              l'a encore relue. Son montant part en facturation quand même — la
              relecture n'a de valeur qu'avant l'émission de la facture. */}
          <Col xs="auto" className="d-flex align-items-center">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="manutentionAValiderCheckbox"
                checked={manutentionAValider}
                onChange={handleManutentionAValiderChange}
              />
              <label className="form-check-label" htmlFor="manutentionAValiderCheckbox">
                Saisie livreur à valider
              </label>
            </div>
          </Col>

          {/* Coche par defaut. Masque les paiements carte echoues ou annules ;
              les paiements simplement en attente restent visibles, avec un badge
              orange — c'est aussi le cas d'une commande creee au back-office pour
              un client hors compte, pas encore declaree payee. */}
          <Col xs="auto" className="d-flex align-items-center">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="masquerPaiementsEchouesCheckbox"
                checked={masquerPaiementsEchoues}
                onChange={(e) => {
                  setMasquerPaiementsEchoues(e.target.checked);
                  setNbr(1);
                }}
              />
              <label className="form-check-label" htmlFor="masquerPaiementsEchouesCheckbox">
                Masquer les paiements échoués
              </label>
            </div>
          </Col>

          <Col xs="auto">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                Colonnes
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {optionalColumns.map(col => (
                  <Dropdown.Item key={col.accessor} as="button" onClick={() => toggleColumn(col.accessor)}>
                    {hiddenColumns.includes(col.accessor) ? "☐" : "☑"} {col.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>

          <Col
            xs
            lg="2"
            className="d-flex justify-content-end align-items-center"
            style={{ alignContent: "right" }}
          ></Col>
        </Row>
         <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />

        {/* Affichage conditionnel selon la vue active */}
        {loading ? (
          // Display loader while data is being fetched
          <p className="lead">Chargement...</p>
        ) : (
          <>
            {/* Vue Liste */}
            {activeView === 'list' && (
              <DynamicTable
                columns={visibleColumns}
                data={commandesData?.data}
                link={commandesData?.links}
                last_page={commandesData?.last_page}
                setNbr={setNbr}
                nbr={nbr}
                total={commandesData?.total}
                name="CommandesAllfournisseurs"
              />
            )}

            {/* Vue Kanban */}
            {activeView === 'kanban' && (
              <KanbanView
                orders={kanbanData?.data || []}
                onStatusChange={handleOrderStatusChange}
                totalDelivered={kanbanData?.total_delivered}
                loadAllDelivered={loadAllDelivered}
                onLoadAllDelivered={handleLoadAllDelivered}
              />
            )}

            {/* Vue Calendrier */}
            {activeView === 'calendar' && (
              <CalendarView
                orders={calendarData?.data || []}
                onEventClick={(order) => {
                  window.location.href = `/commande-fournisseur/show/${order.id}`;
                }}
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
}

// Export the component as the default export
export default ListCommandesAllFournisseur;
